/** Custom workout builder — localStorage + duration estimates (Wellness Gym) */

export const CUSTOM_WORKOUT_PILLARS = [
    'Nervous System Regulation',
    'Mindset & Awareness',
    'Emotional Capacity & Expression',
    'Resilience & Purpose',
    'Connection & Communication',
];

export const STEP_TYPES = {
    SLIDER: 'slider',
    MULTIPLE_CHOICE: 'multipleChoice',
    TEXT_INPUT: 'textInput',
    BREATH_PACER: 'breathPacer',
    REFLECTION_CLOSE: 'reflectionClose',
};

export function customWorkoutsStorageKey(username) {
    return `wellnessGym_customWorkouts_${username || 'anon'}`;
}

export function customWorkoutDraftKey(username) {
    return `wellnessGym_customWorkoutDraft_${username || 'anon'}`;
}

export function newStepId() {
    return `step_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
}

export function newWorkoutId() {
    return `cw_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
}

export function emptyCwDraft() {
    return {
        name: '',
        description: '',
        pillar: CUSTOM_WORKOUT_PILLARS[0],
        steps: [],
        editingId: null,
    };
}

const stepDurations = {
    slider: 30,
    multipleChoice: 20,
    textInput: 60,
    breathPacer: (cycles) => Math.min(5, Math.max(1, Number(cycles) || 1)) * 16,
    reflectionClose: 20,
};

export function stepDurationSeconds(step) {
    if (!step || !step.type) return 0;
    switch (step.type) {
        case STEP_TYPES.SLIDER:
            return stepDurations.slider;
        case STEP_TYPES.MULTIPLE_CHOICE:
            return stepDurations.multipleChoice;
        case STEP_TYPES.TEXT_INPUT:
            return stepDurations.textInput;
        case STEP_TYPES.BREATH_PACER:
            return stepDurations.breathPacer(step.config?.cycles);
        case STEP_TYPES.REFLECTION_CLOSE:
            return stepDurations.reflectionClose;
        default:
            return 0;
    }
}

export function estimateWorkoutMinutesFromSteps(steps) {
    if (!Array.isArray(steps) || !steps.length) return 0;
    const totalSec = steps.reduce((sum, s) => sum + stepDurationSeconds(s), 0);
    return Math.max(1, Math.ceil(totalSec / 60));
}

export function hasReflectionClose(steps) {
    return Array.isArray(steps) && steps.some((s) => s.type === STEP_TYPES.REFLECTION_CLOSE);
}

/** Reflection last; assign order 0..n-1 */
export function normalizeStepsOrder(steps) {
    if (!Array.isArray(steps)) return [];
    const rest = steps.filter((s) => s.type !== STEP_TYPES.REFLECTION_CLOSE);
    const ref = steps.filter((s) => s.type === STEP_TYPES.REFLECTION_CLOSE);
    const merged = [...rest, ...ref];
    return merged.map((s, i) => ({ ...s, order: i }));
}

export function defaultStep(type) {
    const id = newStepId();
    switch (type) {
        case STEP_TYPES.SLIDER:
            return {
                id,
                order: 0,
                type: STEP_TYPES.SLIDER,
                config: { question: '', lowLabel: 'Low', highLabel: 'High' },
            };
        case STEP_TYPES.MULTIPLE_CHOICE:
            return {
                id,
                order: 0,
                type: STEP_TYPES.MULTIPLE_CHOICE,
                config: { question: '', options: ['', ''], multiSelect: false },
            };
        case STEP_TYPES.TEXT_INPUT:
            return {
                id,
                order: 0,
                type: STEP_TYPES.TEXT_INPUT,
                config: { prompt: '', hint: '', required: true, charLimit: 200 },
            };
        case STEP_TYPES.BREATH_PACER:
            return {
                id,
                order: 0,
                type: STEP_TYPES.BREATH_PACER,
                config: { pattern: 'box', cycles: 3, label: '' },
            };
        case STEP_TYPES.REFLECTION_CLOSE:
            return {
                id,
                order: 0,
                type: STEP_TYPES.REFLECTION_CLOSE,
                config: { message: '' },
            };
        default:
            return null;
    }
}

export function loadCustomWorkouts(username) {
    try {
        const raw = localStorage.getItem(customWorkoutsStorageKey(username));
        if (!raw) return [];
        const parsed = JSON.parse(raw);
        return Array.isArray(parsed) ? parsed : [];
    } catch {
        return [];
    }
}

export function saveCustomWorkouts(username, workouts) {
    try {
        localStorage.setItem(customWorkoutsStorageKey(username), JSON.stringify(workouts));
    } catch (e) {
        console.error('saveCustomWorkouts failed', e);
    }
}

export function loadDraft(username) {
    try {
        const raw = localStorage.getItem(customWorkoutDraftKey(username));
        if (!raw) return null;
        return JSON.parse(raw);
    } catch {
        return null;
    }
}

export function saveDraft(username, draft) {
    try {
        localStorage.setItem(customWorkoutDraftKey(username), JSON.stringify(draft));
    } catch (e) {
        console.error('saveDraft failed', e);
    }
}

export function clearDraft(username) {
    try {
        localStorage.removeItem(customWorkoutDraftKey(username));
    } catch (e) {
        console.error('clearDraft failed', e);
    }
}

function nonEmpty(s) {
    return typeof s === 'string' && s.trim().length > 0;
}

export function validateCustomWorkoutForSave(workout) {
    const errors = [];
    if (!workout) {
        errors.push('missing');
        return { ok: false, errors };
    }
    if (!nonEmpty(workout.name) || workout.name.trim().length > 60) errors.push('name');
    if (workout.description && workout.description.length > 140) errors.push('description');
    if (!CUSTOM_WORKOUT_PILLARS.includes(workout.pillar)) errors.push('pillar');
    const steps = normalizeStepsOrder(workout.steps || []);
    if (steps.length < 2) errors.push('minSteps');
    if (steps.length > 6) errors.push('maxSteps');
    const ref = steps.filter((s) => s.type === STEP_TYPES.REFLECTION_CLOSE);
    if (ref.length !== 1) errors.push('reflection');
    if (ref.length && steps[steps.length - 1]?.type !== STEP_TYPES.REFLECTION_CLOSE) errors.push('reflectionLast');

    for (const s of steps) {
        if (!s.config) {
            errors.push('config');
            break;
        }
        if (s.type === STEP_TYPES.SLIDER) {
            if (!nonEmpty(s.config.question) || s.config.question.length > 80) errors.push('sliderQ');
            if (!nonEmpty(s.config.lowLabel) || !nonEmpty(s.config.highLabel)) errors.push('sliderLabels');
        }
        if (s.type === STEP_TYPES.MULTIPLE_CHOICE) {
            if (!nonEmpty(s.config.question) || s.config.question.length > 100) errors.push('mcQ');
            const opts = (s.config.options || []).map((o) => String(o).trim()).filter(Boolean);
            if (opts.length < 2 || opts.length > 5) errors.push('mcOpts');
            if (opts.some((o) => o.length > 60)) errors.push('mcOptLen');
        }
        if (s.type === STEP_TYPES.TEXT_INPUT) {
            if (!nonEmpty(s.config.prompt) || s.config.prompt.length > 120) errors.push('textPrompt');
            if (s.config.hint && s.config.hint.length > 80) errors.push('textHint');
            const lim = Number(s.config.charLimit) || 200;
            if (lim < 1 || lim > 400) errors.push('textLimit');
        }
        if (s.type === STEP_TYPES.BREATH_PACER) {
            if (!['box', 'extendedExhale'].includes(s.config.pattern)) errors.push('breathPattern');
            const c = Number(s.config.cycles);
            if (c < 1 || c > 5) errors.push('breathCycles');
            if (s.config.label && s.config.label.length > 60) errors.push('breathLabel');
        }
        if (s.type === STEP_TYPES.REFLECTION_CLOSE) {
            if (!nonEmpty(s.config.message) || s.config.message.length > 200) errors.push('reflectionMsg');
        }
    }

    return { ok: errors.length === 0, errors };
}
